"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { rtdb } from "@/lib/firebase";
import { ref, onValue, off, query, orderByChild, equalTo, DataSnapshot } from "firebase/database";
import { BillboardScoreboard } from "@/components/billboard-scoreboard";
import { Loader2, AlertCircle } from "lucide-react";
function ScoreboardContent() {
  const searchParams = useSearchParams();
  const gameCodeParam = searchParams.get("gameCode");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gameData, setGameData] = useState<{
    gameState: any;
    playersData: any;
    settingsData: any;
    sport: string;
  } | null>(null);
  const listenersRef = useRef<{
    path: string;
    callback: (snapshot: DataSnapshot) => void;
  }[]>([]);
  const activePathRef = useRef<string | null>(null);
  useEffect(() => {
    if (!gameCodeParam) {
      setError("No game code provided.");
      setLoading(false);
      return;
    }
    const code = gameCodeParam.trim().toUpperCase();
    if (!code) {
      setError("Invalid game code.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setGameData(null);
    activePathRef.current = null;
    listenersRef.current = [];
    const checkedPaths = new Set<string>();

    // 1. Direct Path Search (For Legacy/NFC/OG Codes)
    const directPath = `padelGoldenCourt/${code}`;
    const directPathSport = 'PADEL';

    // 2. Query Search (For Public Lobbies)
    const queryPaths = [{
      root: 'publicPlay/PADEL',
      sport: 'PADEL'
    }, {
      root: 'publicPlay/TENNIS',
      sport: 'TENNIS'
    }, {
      root: 'publicPlay/BADMINTON',
      sport: 'BADMINTON'
    }, {
      root: 'publicPlay/PICKLEBALL',
      sport: 'PICKLEBALL'
    }, {
      root: 'publicPlay/PLATFORM',
      sport: 'PLATFORM'
    }];
    const totalChecks = 1 + queryPaths.length;
    const handleSuccess = (snapshot: DataSnapshot, sport: string, source: string, path: string) => {
      if (activePathRef.current && activePathRef.current !== path) return;
      const data = snapshot.val();
      let matchData = null;
      if (source === 'direct') {
        matchData = data;
      } else {
        const matchIds = Object.keys(data);
        if (matchIds.length > 0) {
          matchData = data[matchIds[0]];
        }
      }
      if (matchData && matchData.gameState) {
        if (!activePathRef.current) {
          activePathRef.current = path;
          // Ideally we would unsubscribe others here, but for simplicity we just ignore them
        }
        const states = matchData.gameState;
        if (states) {
          const keys = Object.keys(states);
          const maxKey = keys.reduce((a, b) => {
            const numA = parseInt(a);
            const numB = parseInt(b);
            return (isNaN(numA) ? -1 : numA) > (isNaN(numB) ? -1 : numB) ? a : b;
          }, "0");
          setGameData({
            gameState: states[maxKey],
            playersData: matchData.players,
            settingsData: matchData.settings,
            sport: sport
          });
          setLoading(false);
        }
      }
    };
    const checkCompletion = (key: string) => {
      if (!checkedPaths.has(key)) {
        checkedPaths.add(key);
      }
      if (!activePathRef.current && checkedPaths.size === totalChecks) {
        // Only set error if we really haven't found anything after checking everything
        // But since listeners are async, this might trigger early if we are not careful.
        // Actually, onValue triggers immediately with null if not found? 
        // Firebase returns null snapshot if not exists.
      }
    };

    // --- EXECUTE DIRECT PATH SEARCH ---
    const directRef = ref(rtdb, directPath);
    const directListener = (snapshot_0: DataSnapshot) => {
      if (activePathRef.current && activePathRef.current !== directPath) return;
      if (snapshot_0.exists()) {
        handleSuccess(snapshot_0, directPathSport, 'direct', directPath);
      } else {
        checkCompletion(directPath);
      }
    };
    onValue(directRef, directListener, err => {
      console.warn(`RTDB Direct Error:`, err);
      checkCompletion(directPath);
    });
    listenersRef.current.push({
      path: directPath,
      callback: directListener
    });

    // --- EXECUTE QUERY SEARCHES ---
    queryPaths.forEach(({
      root,
      sport: sport_0
    }) => {
      const dbRef = ref(rtdb, root);
      const q = query(dbRef, orderByChild("gameCode"), equalTo(code));
      const listener = (snapshot_1: DataSnapshot) => {
        if (activePathRef.current && activePathRef.current !== root) return;
        if (snapshot_1.exists()) {
          handleSuccess(snapshot_1, sport_0, 'query', root);
        }
        checkCompletion(root);
      };
      onValue(q, listener, err_0 => {
        console.warn(`RTDB Query Error:`, err_0);
        checkCompletion(root);
      });
      listenersRef.current.push({
        path: root,
        callback: listener
      });
    });

    // Fallback timeout
    const timeoutId = setTimeout(() => {
      if (!activePathRef.current && loading) {
        setLoading(false);
        setError("Match not found or timed out.");
      }
    }, 5000);
    return () => {
      clearTimeout(timeoutId);
      listenersRef.current.forEach(({
        path: path_0,
        callback
      }) => {
        // Note: This 'off' might not work perfectly for queries as we need the exact Query object
        // For now, we rely on the component unmounting and best-effort cleanup
        // A perfect cleanup requires storing the Query object.
        try {
          // We can't easily reconstruction the Query object here without storing it.
          // But since we are unmounting, it's less critical if we don't spam.
          // For the direct ref it works.
          off(ref(rtdb, path_0), 'value', callback);
        } catch (e) {
          // Ignore
        }
      });
    };
  }, [gameCodeParam]);
  if (error) {
    return <div className="flex flex-col items-center justify-center h-screen bg-black text-white p-4">
              <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
              <h1 className="text-2xl font-bold mb-2">Error</h1>
              <p className="text-gray-400">{error}</p>
          </div>;
  }
  if (loading && !gameData) {
    return <div className="flex flex-col items-center justify-center h-screen bg-black text-white p-4">
              <Loader2 className="w-12 h-12 animate-spin text-[#B0D18B] mb-4" />
              <p className="text-gray-400 animate-pulse">Connecting to scoreboard...</p>
          </div>;
  }
  return <div className="min-h-screen bg-black flex items-center justify-center">
        {gameData && <BillboardScoreboard gameState={gameData.gameState} playersData={gameData.playersData} settingsData={gameData.settingsData} sport={gameData.sport} />}
    </div>;
}
export default function ScoreboardPage() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={<div className="min-h-screen bg-black" />}><ScoreboardContent /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}